/**
 * Product Availability Web Component
 *
 * Displays product availability status (online + in-store pickup).
 * Listens for location change events from preferred-location.js
 * and updates the UI state accordingly.
 */
const availabilityStrings = theme.strings.availability;

class ProductAvailability extends HTMLElement {
  connectedCallback() {
    this._abort = new AbortController();
    this._availabilities = this.parseAvailabilities();
    this.bindEvents();
  }

  disconnectedCallback() {
    this._abort?.abort();
    this._abort = null;
  }

  get state() {
    return this.dataset.state || "no-location";
  }

  get locationName() {
    return this.dataset.locationName || "";
  }

  get inventoryThreshold() {
    return parseInt(this.dataset.inventoryThreshold, 10) || 10;
  }

  parseAvailabilities() {
    try {
      return JSON.parse(this.dataset.availabilities || "{}");
    } catch {
      return {};
    }
  }

  isAvailableAt(locationId) {
    return this._availabilities[locationId]?.available === true;
  }

  getLocationData(locationId) {
    return this._availabilities[locationId] || null;
  }

  bindEvents() {
    const signal = this._abort.signal;

    document.addEventListener("store:location-changed", (e) => this.handleLocationChange(e.detail), { signal });

    this.addEventListener(
      "click",
      (e) => {
        if (e.target.closest(".js-availability-update-location")) {
          this.handleUpdateLocation();
        }
      },
      { signal },
    );
  }

  handleLocationChange(detail) {
    const { locationId, locationName } = detail;

    this.dataset.locationId = locationId || "";
    this.dataset.locationName = locationName || "";

    if (!locationId) {
      this.dataset.state = "no-location";
    } else if (this.isAvailableAt(locationId)) {
      this.dataset.state = "available";
    } else {
      this.dataset.state = "not-available";
    }

    this.updateLocationLabels(locationName);
    this.updateAlternateLocationText(locationId);
    this.updateStockCount(locationId);
    this.updatePickupTime(locationId);
  }

  updateLocationLabels(locationName) {
    this.querySelectorAll(".js-availability-location-name").forEach((el) => {
      el.textContent = locationName || "";
    });
  }

  updateAlternateLocationText(currentLocationId) {
    const el = this.querySelector(".js-availability-alternate-text");
    if (!el) return;

    let alternateName = "";
    for (const [locId, locData] of Object.entries(this._availabilities)) {
      if (locId !== currentLocationId && locData.available) {
        alternateName = locData.name;
        break;
      }
    }

    if (alternateName) {
      el.innerHTML = availabilityStrings.toSeeAvailabilityAtHtml.replace(/\{\{\s*location_name\s*\}\}/, alternateName);
    } else {
      el.textContent = availabilityStrings.toSeeAvailability;
    }
  }

  updateStockCount(locationId) {
    const wrappers = this.querySelectorAll(".js-availability-stock-wrapper");
    if (!wrappers.length) return;

    const stockCount = parseInt(this.dataset.stockCount, 10) || 0;
    const shouldShow = this.isAvailableAt(locationId) && stockCount > 0 && stockCount <= this.inventoryThreshold;

    wrappers.forEach((wrapper) => {
      wrapper.hidden = !shouldShow;

      if (shouldShow) {
        const countEl = wrapper.querySelector(".js-availability-stock-count");
        if (countEl) {
          countEl.textContent = `${stockCount} ${availabilityStrings.leftInStock}`;
        }
      }
    });
  }

  updatePickupTime(locationId) {
    const el = this.querySelector(".js-availability-pickup-time");
    if (!el) return;

    const locData = this.getLocationData(locationId);
    if (locData?.pickUpTime) {
      el.textContent = locData.pickUpTime;
    }
  }

  handleUpdateLocation() {
    const preferredLocation = document.querySelector("preferred-location");
    if (!preferredLocation) return this.openLocationPicker();

    const cards = preferredLocation.cards;
    const currentLocationId = this.dataset.locationId;

    if (cards.length === 2 && currentLocationId) {
      const otherCard = cards.find((card) => card.dataset.locationId !== currentLocationId);
      if (otherCard) {
        preferredLocation.setPreferredLocation(otherCard);
        return;
      }
    }

    this.openLocationPicker();
  }

  openLocationPicker() {
    const disclosure = document.querySelector(".js-header-locations");
    if (disclosure) {
      const details = disclosure.querySelector("details");
      if (details) {
        details.open = true;
      }
    }
  }
}

if (!customElements.get("pickup-availability")) {
  customElements.define("pickup-availability", ProductAvailability);
}
